import { useState } from 'react';
import styled from 'styled-components';
import { CollapseIcon } from '../../../components/Icons/CollapseIcon';
import { ExpandIcon } from '../../../components/Icons/ExpandIcon';
import { DragIndicator } from '../../CreateVehicle/icons/DragIndicator';
import { useField } from 'react-final-form';
import { GradeField } from '../components/grade';
import { PhotosView } from '../components/photos';
import { CheckboxQuestions } from '../components/checkbox/CheckboxField';
import { NotesIcon } from '../icons/notes';
import { NotesField } from '../components/notes';
import { TiresGradeSelection } from '../components/tires/gradeSelection';
import { SoundRecordingProvider, useSoundRecordingContext } from '../SoundRecorder/context';
import { SoundRecorder } from '../SoundRecorder';
import { OdometerImages } from '../OdometerImages';
import { RecordingPlayer } from '../SoundRecorder/RecordingPlayer';
import { SoundPlayer } from '../components/soundPlayer';
import { useGetVehicleByIdQuery } from 'services/api/upload-vehicle';
import { LicensePlateDate } from '../components/tires/licensePlateDate';
import { useVehicleEditing } from 'context/isAnotherUserEditingContext';
import { NotesTopIcon } from 'components/VehicleCommon/Notes/NotesTopIcon';

type Props = {
  sectionId: string;
  theme?: 'white' | 'grey';
  index?: number;
  notesOpened?: boolean;
  aiNotes?: boolean;
};

export const Wrapper = styled.div<{ withBorder?: boolean; background: string; disabled?: boolean }>`
  background: ${props => props.background};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 8px 9px 8px;
  width: 100%;
  outline: ${props => (props.withBorder ? '1px solid #3994de' : 'none')};
  pointer-events: ${props => (props.disabled ? '0.7' : 1)};
`;

const HeaderControls = styled.div<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  margin-left: auto;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`;

const IconWrapper = styled.div<{ withAutoMargin?: boolean }>`
  display: flex;
  position: relative;
  margin-left: ${props => (props.withAutoMargin ? 'auto' : 0)};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 14.06px;
  color: #000;
  margin-left: 8px;
`;

const fillColor = 'rgba(153, 153, 153, 1)';

const AutoCheckButton = styled.div`
  background: #3994de;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 14.06px;
  color: #fff;
  height: 32px;
  width: 100%;
`;

export const themeOptions = {
  white: {
    background: '#fff',
    gradeBackground: '#2222220A'
  },
  grey: {
    background: '#F5F5F5',
    gradeBackground: '#fff'
  }
};

const WrapContext = ({ children, sectionId }: { children: React.ReactNode; sectionId: string }) => {
  const withSoundRecorder = sectionId.includes('how_engine_runs_overall');
  if (withSoundRecorder) return <SoundRecordingProvider name={sectionId}>{children}</SoundRecordingProvider>;

  return <>{children}</>;
};

export const Level3Section = ({ sectionId, theme = 'grey', index, aiNotes, notesOpened = false }: Props) => {
  const { background, gradeBackground } = themeOptions[theme];

  const [isExpanded, setIsExpanded] = useState(true);
  const [isNotesFieldOpen, setIsNotesFieldOpen] = useState(notesOpened);

  const [isPhotoMode, setPhotoMode] = useState(false);

  const toggleNotes = () => setIsNotesFieldOpen(curr => !curr);
  const toggleExpanded = () => setIsExpanded(curr => !curr);
  const togglePhotoMode = () => setPhotoMode(curr => !curr);

  const { hasAnotherEditor } = useVehicleEditing();

  const handleNotesClick = () => {
    if (isPhotoMode) {
      setPhotoMode(false);
      setIsNotesFieldOpen(true);
    } else {
      toggleNotes();
    }
  };

  const { input: notesInput } = useField(`${sectionId}.notes.message`);
  const {
    input: { value: notesStatus }
  } = useField(`${sectionId}.notes.status`);

  const onGradeSelect = (grade: string) => {
    if (grade === 'poor' || grade === 'fair') {
      setIsNotesFieldOpen(true);
    }
  };

  const {
    input: { value: label }
  } = useField(`${sectionId}.label`);

  const showGradeSelection = sectionId?.endsWith('_tire') && sectionId?.startsWith('exterior.subsections.tires');
  const showPlateDate = sectionId === 'exterior.subsections.tires.subsections.license_plate_registration';

  const withBorder = theme === 'white';
  const withSoundRecorder = sectionId.includes('how_engine_runs_overall');

  const showOgTextLabel = aiNotes && notesStatus === 'ai_used';

  return (
    <WrapContext sectionId={sectionId}>
      <Wrapper withBorder={withBorder} background={background}>
        <HeaderRow>
          <IconWrapper onClick={toggleExpanded}>
            {isExpanded ? <CollapseIcon fillColor={fillColor} /> : <ExpandIcon fillColor={fillColor} />}
          </IconWrapper>
          <Title>{label}</Title>
          <HeaderControls disabled={hasAnotherEditor}>
            {showOgTextLabel && <OriginalTextLabel>Original Text</OriginalTextLabel>}
            {showGradeSelection && (
              <IconWrapper>
                <TiresGradeSelection sectionId={sectionId} />
              </IconWrapper>
            )}
            {showPlateDate && <LicensePlateDate sectionId={sectionId} />}
            {withSoundRecorder && <SoundRecorder />}
            <IconWrapper onClick={handleNotesClick}>
              <NotesIcon isActive={isNotesFieldOpen && !isPhotoMode && isExpanded} />
              {notesInput.value && <NotesTopIcon iconType={notesStatus === 'ai_used' ? 'ai' : 'checkmark'} />}
            </IconWrapper>
            <IconWrapper>
              <DragIndicator />
            </IconWrapper>
          </HeaderControls>
        </HeaderRow>
        {isExpanded && !isPhotoMode && (
          <GeneralSection
            theme={theme}
            gradeBackground={gradeBackground}
            sectionId={sectionId}
            aiNotes={aiNotes}
            isNotesFieldOpen={isNotesFieldOpen}
            onGradeSelect={onGradeSelect}
          />
        )}
        {isExpanded && isPhotoMode && <PhotosView sectionId={sectionId} />}
      </Wrapper>
    </WrapContext>
  );
};

const GeneralSection = ({
  sectionId,
  isNotesFieldOpen,
  onGradeSelect,
  gradeBackground,
  aiNotes,
  theme = 'grey'
}: {
  sectionId: string;
  isNotesFieldOpen: boolean;
  onGradeSelect: (grade: string) => void;
  gradeBackground: string;
  theme: 'grey' | 'white';
  aiNotes?: boolean;
}) => {
  const subSectionId = sectionId.split('.')[2];
  const renderAutoCheckReport = subSectionId === 'title_issues';
  const renderCheckboxes = ['title_issues', 'power_equipment_checked'].includes(subSectionId);
  const renderOdometerImages = ['odometer'].includes(subSectionId);
  const renderGrades = !['odometer'].includes(subSectionId);
  const withSoundRecorder = sectionId.includes('how_engine_runs_overall');

  const {
    input: { value: vehicleId }
  } = useField(`vehicleId`);
  const { data: vehicle } = useGetVehicleByIdQuery(vehicleId, { skip: !vehicleId });

  // const [fetchReport] = useGetAutoCheckReportMutation();

  const { hasAnotherEditor } = useVehicleEditing();

  const openAutocheckReport = async (e: any) => {
    if (!vehicle) return;
    e.preventDefault();
    window.open(`/vehicles/${vehicle.vin}/autocheck-report`, '_blank');
  };

  return (
    <>
      {withSoundRecorder && <AudioComponent sectionId={sectionId} />}
      {isNotesFieldOpen && <NotesField aiNotes={aiNotes} sectionId={sectionId} />}
      {!isNotesFieldOpen && <div />}
      {renderGrades && <GradeField sectionId={sectionId} onSelect={onGradeSelect} background={gradeBackground} />}
      {renderAutoCheckReport && <AutoCheckButton onClick={openAutocheckReport}>View AutoCheck Report</AutoCheckButton>}
      {renderCheckboxes && <CheckboxQuestions sectionId={sectionId} disabled={hasAnotherEditor} />}
      {renderOdometerImages && <OdometerImages theme={theme} />}
    </>
  );
};

const AudioComponent = ({ sectionId }: { sectionId: string }) => {
  const { isRecording } = useSoundRecordingContext();
  const {
    input: { value: audioUrl }
  } = useField(`${sectionId}.audio_url`);

  return (
    <>
      {isRecording && <RecordingPlayer />}
      {audioUrl && !isRecording && <SoundPlayer sectionId={sectionId} />}
    </>
  );
};

const OriginalTextLabel = styled.div`
  display: flex;
  border-radius: 8px;
  background: #ccccccb2;
  color: #000;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  width: 75px;
  letter-spacing: 0%;
  margin-right: 4px;
  height: 24px;
  align-items: center;
  justify-content: center;
`;
